import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { GlitchText, TerminalText, ASCIIBackground, useGlyphScramble, GlyphDisplay } from "./animations"

const MotionLink = motion(Link)

const MESSAGES = [
  { type: "user", text: "Invoice Gary $300 for bathroom work" },
  { type: "demon", text: "Got it! Invoice sent to Gary." },
  { type: "demon", text: "I'll let you know when he pays." },
]

export function HeroSection() {
  const [visibleMessages, setVisibleMessages] = useState(0)
  const [showCtas, setShowCtas] = useState(false)

  const { characters: getStartedChars, display: getStartedDisplay, startLoop: startGetStarted, stopLoop: stopGetStarted } =
    useGlyphScramble("Get Started", { scrambleInterval: 28 })

  const { characters: howChars, display: howDisplay, startLoop: startHow, stopLoop: stopHow } =
    useGlyphScramble("See How It Works", { scrambleInterval: 28 })

  useEffect(() => {
    const timers: NodeJS.Timeout[] = []
    MESSAGES.forEach((_, index) => {
      timers.push(
        setTimeout(() => {
          setVisibleMessages(index + 1)
        }, 1200 + index * 1000)
      )
    })
    
    // Show CTAs after initial animation
    timers.push(setTimeout(() => setShowCtas(true), 800))
    
    return () => timers.forEach(clearTimeout)
  }, [])

  return (
    <section
      className="relative min-h-screen flex items-center justify-center pt-20"
      style={{
        background: "linear-gradient(180deg, var(--bg) 0%, rgba(20, 8, 12, 1) 100%)",
      }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 80% 50% at 50% 30%, rgba(255, 43, 79, 0.08), transparent)",
        }}
      />

      {/* ASCII Art Background */}
      <ASCIIBackground className="hidden lg:block" style={{ right: "8%", top: "15%" }} />

      <div
        className="relative z-10 px-6 py-20 grid gap-16 lg:grid-cols-2 items-center w-full mx-auto"
        style={{ maxWidth: "1200px" }}
      >
        {/* Copy */}
        <motion.div
          className="flex flex-col gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div
            className="mono text-xs"
            style={{
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--accent-text)",
            }}
          >
            AI-Powered Back Office
          </div>

          <h1
            className="mono"
            style={{
              fontSize: "clamp(36px, 6vw, 56px)",
              lineHeight: 1.1,
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            <GlitchText delay={400}>Text your demon.</GlitchText>
            <br />
            <span style={{ color: "var(--accent-text)" }}>
              <GlitchText delay={600}>Get paid faster.</GlitchText>
            </span>
          </h1>

          <p
            className="mono"
            style={{
              color: "var(--muted)",
              maxWidth: "480px",
              lineHeight: 1.7,
              fontSize: "14px",
            }}
          >
            <TerminalText 
              text="Stop chasing invoices. Your demon handles billing, collections, and customer communication—all via SMS. No app required."
              delay={800}
              speed={12}
            />
          </p>

          <motion.div 
            className="flex flex-wrap gap-4 mt-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: showCtas ? 1 : 0, y: showCtas ? 0 : 12 }}
            transition={{ duration: 0.6 }}
          >
            <MotionLink
              to="/onboard/intro"
              className="btn-lg btn-primary mono"
              onPointerEnter={startGetStarted}
              onPointerLeave={stopGetStarted}
              whileHover={{ scale: 1.04, boxShadow: "0 0 28px rgba(255, 43, 79, 0.5)" }}
              whileTap={{ scale: 0.96 }}
              style={{
                padding: "14px 28px",
                fontSize: "13px",
              }}
            >
              <span className="glitch-typewriter">
                <GlyphDisplay characters={getStartedChars} display={getStartedDisplay} prefix="cta-start" />
              </span>
            </MotionLink>
            <MotionLink
              to="#how-it-works"
              className="btn-lg btn-secondary mono"
              onPointerEnter={startHow}
              onPointerLeave={stopHow}
              whileHover={{ scale: 1.03, borderColor: "rgba(255, 43, 79, 0.6)", boxShadow: "0 0 0 1px rgba(255, 43, 79, 0.4)" }}
              whileTap={{ scale: 0.97 }}
              style={{
                padding: "14px 28px",
                fontSize: "13px",
              }}
            >
              <span className="glitch-typewriter">
                <GlyphDisplay characters={howChars} display={howDisplay} prefix="cta-how" />
              </span>
            </MotionLink>
          </motion.div>
        </motion.div>

        {/* Phone mockup */}
        <motion.div
          className="flex justify-center lg:justify-end"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div
            className="relative"
            style={{
              width: "280px",
              padding: "16px 14px 28px",
              background: "rgba(18, 18, 22, 0.95)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              boxShadow: "0 32px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 43, 79, 0.1)",
            }}
          >
            {/* Corner accents */}
            <span className="corner top-left" style={{ position: "absolute", top: -1, left: -1, width: 12, height: 12, borderTop: "2px solid var(--accent)", borderLeft: "2px solid var(--accent)" }} />
            <span className="corner top-right" style={{ position: "absolute", top: -1, right: -1, width: 12, height: 12, borderTop: "2px solid var(--accent)", borderRight: "2px solid var(--accent)" }} />
            <span className="corner bottom-left" style={{ position: "absolute", bottom: -1, left: -1, width: 12, height: 12, borderBottom: "2px solid var(--accent)", borderLeft: "2px solid var(--accent)" }} />
            <span className="corner bottom-right" style={{ position: "absolute", bottom: -1, right: -1, width: 12, height: 12, borderBottom: "2px solid var(--accent)", borderRight: "2px solid var(--accent)" }} />

            {/* Speaker */}
            <div
              className="mx-auto mb-4"
              style={{
                width: "60px",
                height: "4px",
                background: "rgba(255, 255, 255, 0.2)",
              }}
            />

            {/* Screen */}
            <div
              className="p-4 grid gap-3"
              style={{
                background: "var(--panel)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                minHeight: "320px",
              }}
            >
              {/* Status bar */}
              <div
                className="flex justify-between items-center pb-3 mb-2"
                style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}
              >
                <span className="mono text-xs" style={{ color: "var(--muted)", letterSpacing: "0.1em" }}>
                  DEMON
                </span>
                <motion.span
                  className="mono text-xs px-2 py-1"
                  animate={{ opacity: [1, 0.6, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    background: "rgba(255, 43, 79, 0.15)",
                    border: "1px solid rgba(255, 43, 79, 0.3)",
                    color: "var(--accent-text)",
                    letterSpacing: "0.08em",
                  }}
                >
                  ACTIVE
                </motion.span>
              </div>

              {/* Messages */}
              <div className="grid gap-3">
                {MESSAGES.slice(0, visibleMessages).map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="p-3 text-sm mono"
                    style={{
                      background:
                        msg.type === "user"
                          ? "var(--accent)"
                          : "rgba(255, 255, 255, 0.06)",
                      color: msg.type === "user" ? "#0a0a0b" : "var(--text)",
                      border:
                        msg.type === "user"
                          ? "none"
                          : "1px solid rgba(255, 255, 255, 0.08)",
                      alignSelf: msg.type === "user" ? "flex-end" : "flex-start",
                      maxWidth: "90%",
                      fontSize: "12px",
                    }}
                  >
                    {msg.text}
                  </motion.div>
                ))}

                {visibleMessages < MESSAGES.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-1 p-3"
                    style={{ color: "var(--muted)" }}
                  >
                    <span className="typing-indicator">
                      <span>.</span><span>.</span><span>.</span>
                    </span>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Home indicator */}
            <div
              className="mx-auto mt-4"
              style={{
                width: "40px",
                height: "4px",
                background: "rgba(255, 255, 255, 0.15)",
              }}
            />
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <a
          href="#how-it-works"
          className="mono text-xs flex flex-col items-center gap-2"
          style={{ color: "var(--muted)", letterSpacing: "0.1em" }}
        >
          <span>SCROLL</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            ↓
          </motion.span>
        </a>
      </motion.div>
    </section>
  )
}
